import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Site, sitesApi } from "../../lib/sites";
import LocationPicker, {
  type AreaMapEditorConfig,
  type AreaMapPlacement,
} from "./LocationPicker";
import AddressAutocomplete from "./AddressAutocomplete";
import { reverseGeocode, formatAddress } from "../../lib/geocoding";
import { siteControllersApi } from "../../lib/site-controllers";
import { areasApi, AreaResponse, UpdateAreaData } from "../../lib/areas";
import { hasFullMapPlacement } from "./location-map-shared";
import { useAuth } from "../../hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface SitesLocationProps {
  site: Site;
  canEdit?: boolean;
  onError?: (message: string) => void;
}

function toPlacement(a: AreaResponse): AreaMapPlacement | null {
  if (
    !hasFullMapPlacement(a) ||
    a.latitude == null ||
    a.longitude == null ||
    !a.shape ||
    a.widthM == null ||
    a.heightM == null ||
    a.orientationDegrees == null
  ) {
    return null;
  }
  return {
    areaId: a.id,
    name: a.name,
    latitude: a.latitude,
    longitude: a.longitude,
    shape: a.shape,
    widthM: a.widthM,
    heightM: a.heightM,
    orientationDegrees: a.orientationDegrees,
  };
}

export default function SitesLocation({
  site,
  canEdit = false,
  onError,
}: SitesLocationProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEditAreaMap = user?.role === "super_user";
  const canDragSitePin = canEdit && user?.role === "super_user";

  const [formData, setFormData] = useState({
    address: site.address || "",
    latitude: site.latitude?.toString() || "",
    longitude: site.longitude?.toString() || "",
  });
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hasChangesRef = useRef(false);
  const formDataRef = useRef(formData);
  const focusedFieldRef = useRef<string | null>(null);
  const justSavedRef = useRef(false);

  const { data: controllers } = useQuery({
    queryKey: ["site-controllers", site.id],
    queryFn: () => siteControllersApi.getBySite(site.id),
    enabled: !!site.id,
  });

  const { data: allAreas } = useQuery({
    queryKey: ["all-areas", site.id, controllers?.map((c) => c.id).join(",")],
    queryFn: async () => {
      if (!controllers?.length) return [];
      const lists = await Promise.all(
        controllers.map((c) => areasApi.getBySiteController(c.id)),
      );
      return lists.flat();
    },
    enabled: !!site.id && !!controllers?.length,
  });

  const patchAreaMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateAreaData }) =>
      areasApi.update(id, patch),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["all-areas", site.id] });
      queryClient.invalidateQueries({
        queryKey: ["areas-by-controller", site.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["areas", updated.siteControllerId],
      });
    },
    onError: (error: unknown) => {
      const msg =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to update area";
      onError?.(msg);
    },
  });

  const handlePatchArea = useCallback(
    async (areaId: string, patch: UpdateAreaData) => {
      await patchAreaMutation.mutateAsync({ id: areaId, patch });
    },
    [patchAreaMutation],
  );

  const areaMapEditor: AreaMapEditorConfig | undefined = useMemo(() => {
    if (allAreas === undefined) return undefined;
    const lightingAreas = allAreas.filter((a) => a.type === "light");
    const placed = lightingAreas
      .map(toPlacement)
      .filter((p): p is AreaMapPlacement => p != null);
    const candidates = lightingAreas
      .filter((a) => !hasFullMapPlacement(a))
      .map((a) => ({ id: a.id, name: a.name }));
    return {
      placed,
      candidates,
      onPatchArea: handlePatchArea,
      disabled: !canEditAreaMap,
      isPending: patchAreaMutation.isPending,
    };
  }, [allAreas, canEditAreaMap, handlePatchArea, patchAreaMutation.isPending]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Site>) => sitesApi.update(site.id, data),
    onSuccess: (updatedSite) => {
      const newFormData = {
        address: updatedSite.address || "",
        latitude: updatedSite.latitude?.toString() || "",
        longitude: updatedSite.longitude?.toString() || "",
      };
      formDataRef.current = newFormData;
      setFormData(newFormData);
      hasChangesRef.current = false;
      justSavedRef.current = true;
      queryClient.setQueryData(["site", site.id], updatedSite);
      queryClient.invalidateQueries({ queryKey: ["site", site.id] });
      setTimeout(() => {
        justSavedRef.current = false;
      }, 1000);
      if (focusedFieldRef.current) {
        setTimeout(() => {
          const input = document.getElementById(
            `location-${focusedFieldRef.current}`,
          );
          if (input) {
            input.focus();
          }
        }, 0);
      }
    },
    onError: (error: unknown) => {
      console.error("Error updating site location:", error);
      const errorMessage =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to update site";
      onError?.(errorMessage);
    },
  });

  const buildLocationPayload = () => {
    const currentFormData = formDataRef.current;
    return {
      address: currentFormData.address || undefined,
      latitude: currentFormData.latitude
        ? parseFloat(currentFormData.latitude)
        : undefined,
      longitude: currentFormData.longitude
        ? parseFloat(currentFormData.longitude)
        : undefined,
    };
  };

  const saveChanges = () => {
    if (!hasChangesRef.current) {
      return;
    }
    updateMutation.mutate(buildLocationPayload());
  };

  const debouncedSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      if (hasChangesRef.current) {
        updateMutation.mutate(buildLocationPayload());
      }
      if (focusedFieldRef.current) {
        setTimeout(() => {
          const input = document.getElementById(
            `location-${focusedFieldRef.current}`,
          );
          if (input) {
            input.focus();
          }
        }, 0);
      }
    }, 1000);
  };

  const handleFieldChange = (field: string, value: string) => {
    if (!focusedFieldRef.current) {
      focusedFieldRef.current = field;
    }
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      formDataRef.current = newData;
      return newData;
    });
    hasChangesRef.current = true;
    if (canEdit) {
      debouncedSave();
    }
    if (focusedFieldRef.current === field) {
      setTimeout(() => {
        const input = document.getElementById(`location-${field}`);
        const activeElement = document.activeElement;
        if (
          input &&
          activeElement !== input &&
          activeElement !== document.body
        ) {
          input.focus();
          if (input instanceof HTMLInputElement) {
            const length = input.value.length;
            input.setSelectionRange(length, length);
          }
        }
      }, 0);
    }
  };

  const handleFieldBlur = (field: string) => {
    if (focusedFieldRef.current === field) {
      focusedFieldRef.current = null;
    }
    if (hasChangesRef.current) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = undefined;
      }
      saveChanges();
    }
  };

  const handleFieldFocus = (field: string) => {
    focusedFieldRef.current = field;
  };

  const handleAddressSelect = (
    latitude: number,
    longitude: number,
    address: string,
  ) => {
    if (!canEdit) return;
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        address,
        latitude: latitude.toString(),
        longitude: longitude.toString(),
      };
      formDataRef.current = newFormData;
      hasChangesRef.current = true;
      return newFormData;
    });
    setTimeout(() => {
      saveChanges();
    }, 300);
  };

  const handleMapClick = async (lat: number, lng: number) => {
    if (!canDragSitePin) return;
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        latitude: lat.toString(),
        longitude: lng.toString(),
      };
      formDataRef.current = newFormData;
      hasChangesRef.current = true;
      return newFormData;
    });
    try {
      const result = await reverseGeocode(lat, lng);
      if (result) {
        const formattedAddress = formatAddress(result);
        setFormData((prev) => {
          const newFormData = {
            ...prev,
            address: formattedAddress,
          };
          formDataRef.current = newFormData;
          return newFormData;
        });
      }
    } catch {
      // coordinates still updated
    }
    setTimeout(() => {
      saveChanges();
    }, 500);
  };

  useEffect(() => {
    if (justSavedRef.current) {
      return;
    }
    if (!hasChangesRef.current) {
      const current = formDataRef.current;
      const currentLat = current.latitude ? parseFloat(current.latitude) : null;
      const currentLng = current.longitude
        ? parseFloat(current.longitude)
        : null;
      const siteLat = site.latitude ?? null;
      const siteLng = site.longitude ?? null;
      const hasChanged =
        (site.address || "") !== current.address ||
        siteLat !== currentLat ||
        siteLng !== currentLng;
      if (hasChanged) {
        const newFormData = {
          address: site.address || "",
          latitude: site.latitude?.toString() || "",
          longitude: site.longitude?.toString() || "",
        };
        formDataRef.current = newFormData;
        setFormData(newFormData);
      }
    }
  }, [site]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const mapBusy = updateMutation.isPending || patchAreaMutation.isPending;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Location
            </CardTitle>
            {mapBusy && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="location-address">Address</Label>
            {canEdit ? (
              <>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address) => handleFieldChange("address", address)}
                  onLocationSelect={handleAddressSelect}
                  placeholder="Start typing an address..."
                  disabled={updateMutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Search for an address to automatically set coordinates, or
                  enter manually
                </p>
              </>
            ) : (
              <div className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-muted-foreground">{site.address || "—"}</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {/* <Label>
              Map {canEdit && "(click map or drag the site pin to move)"}
            </Label> */}
            <LocationPicker
              latitude={
                formData.latitude ? parseFloat(formData.latitude) : undefined
              }
              longitude={
                formData.longitude ? parseFloat(formData.longitude) : undefined
              }
              onLocationChange={canDragSitePin ? handleMapClick : undefined}
              areaMapEditor={areaMapEditor}
              mapHeightClassName="min-h-[22rem] h-[28rem]"
            />
            {allAreas !== undefined && allAreas.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {canEditAreaMap
                  ? "Place area overlays on the map below. Adding a placement only sets optional map fields on an existing area."
                  : "Area map overlays are shown read-only. Only super users can edit placements."}
              </p>
            )}
            {canEdit && (
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-foreground">
                    Site Location
                  </p>
                  {/* <p className="text-xs text-muted-foreground">Manual entry for latitude and longitude (decimal degrees).</p> */}
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="location-latitude" className="text-xs">
                    Latitude
                  </Label>
                  <Input
                    id="location-latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) =>
                      handleFieldChange("latitude", e.target.value)
                    }
                    onFocus={() => handleFieldFocus("latitude")}
                    onBlur={() => handleFieldBlur("latitude")}
                    placeholder="e.g., -37.8136"
                    disabled={updateMutation.isPending}
                  />
                  <Label htmlFor="location-longitude" className="text-xs">
                    Longitude
                  </Label>
                  <Input
                    id="location-longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) =>
                      handleFieldChange("longitude", e.target.value)
                    }
                    onFocus={() => handleFieldFocus("longitude")}
                    onBlur={() => handleFieldBlur("longitude")}
                    placeholder="e.g., 144.9631"
                    disabled={updateMutation.isPending}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
